import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import envConfig from '../config/envConfig';
import { DEMO_USERS } from '../data/mockData';
import { ExpertCard, Stars } from '../components/UI';
import { Search, ArrowRight, ArrowLeft, ChevronDown, X, Phone, MessageCircle } from 'lucide-react';


const FAQ_LEARNER = [
  { q: 'How do I book a session?', a: 'Browse experts, pick a profile, select an available slot, and confirm your booking. You\'ll receive a confirmation instantly.' },
  { q: 'Can I reschedule or cancel a booking?', a: 'Yes. You can reschedule or cancel up to 24 hours before the session from your dashboard without any penalty.' },
  { q: 'Are the experts verified?', a: 'All experts go through a profile review and credential check before they can accept bookings on the platform.' },
  { q: 'What session formats are available?', a: 'You can book 1:1 sessions or join group sessions, both conducted live via video call.' },
  { q: 'What if I\'m not satisfied with a session?', a: 'Reach out to our support team within 24 hours of the session and we\'ll review your concern and offer a resolution.' },
];
const FAQ_TUTOR = [
  { q: 'How do I sign up as an Expert?', a: 'Click "Sign up", choose the Expert role, complete your profile, and add your services. You can start accepting bookings right away.' },
  { q: 'How do I set my availability?', a: 'Use the Calendar & Slots section in your dashboard to add or remove available time slots on any date.' },
  { q: 'How and when do I get paid?', a: 'Payouts are processed monthly to your registered bank account after deducting the platform fee shown in your Earnings dashboard.', link: '/tutor/payment', linkLabel: 'See full payout details →' },
  { q: 'Can I set my own rates?', a: 'Yes. You set your own price per service, within the platform\'s price bands for your category.' },
  { q: 'What happens if a learner doesn\'t show up?', a: 'No-show policies protect your time — if a learner misses a confirmed session, you are still compensated as per our cancellation policy.' },
];

function FaqAccordion({ items }) {
  const [open, setOpen] = useState(null);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {items.map((item, i) => (
        <div key={i} style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', background: 'var(--surface)' }}>
          <button
            onClick={() => setOpen(open === i ? null : i)}
            style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', gap: 12 }}
          >
            <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--ink)' }}>{item.q}</span>
            <span style={{ fontSize: 18, color: 'var(--saffron)', flexShrink: 0, transition: 'transform 0.2s', transform: open === i ? 'rotate(45deg)' : 'none' }}>+</span>
          </button>
          {open === i && (
            <div style={{ padding: '0 20px 16px', fontSize: 13, color: 'var(--ink3)', lineHeight: 1.7 }}>
              {item.a}
              {item.link && (
                <Link to={item.link} style={{ display: 'inline-block', marginTop: 8, color: 'var(--saffron)', fontWeight: 600, textDecoration: 'none' }}>
                  {item.linkLabel}
                </Link>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function Landing() {
  const { categories, languages, experts, contentLoading, selectedCity, setSelectedCity, login, toast, user } = useApp();
  const navigate = useNavigate();
  const [search, setSearch]       = useState('');
  const [selCat, setSelCat]       = useState('');
  const [selSubcat, setSelSubcat] = useState('');
  const [selLang, setSelLang]     = useState('');
  const [sort, setSort]           = useState('rating');
  const [expertsPage, setExpertsPage] = useState(0);
  const [customerCareNumbers, setCustomerCareNumbers] = useState([]);
  const EXPERTS_PER_PAGE = 9;

  // Load customer care numbers on component mount
  useEffect(() => {
    const loadCustomerCare = async () => {
      try {
        const response = await fetch(`${envConfig.apiBaseUrl}/content/customer-care`);
        const data = await response.json();
        if (data.success && data.data) {
          setCustomerCareNumbers(data.data);
        }
      } catch (err) {
        console.error('Failed to load customer care numbers:', err);
      }
    };
    loadCustomerCare();
  }, []);

  // Quick demo login handler
  const handleDemoLogin = (demoRole) => {
    const demoUser = DEMO_USERS[demoRole];
    if (demoUser) {
      login(demoUser);
      toast(`Welcome, ${demoUser.name}! 👋`, 'success');
      navigate(demoUser.role === 'expert' ? '/expert' : '/learner');
    }
  };

  const handlePostRequirementClick = () => {
    if (!user) {
      toast('Please log in to post a requirement', 'info');
      navigate('/auth');
      return;
    }
    if (user.role !== 'learner') {
      toast('Only learners can post requirements', 'error');
      return;
    }
    navigate('/learner', { state: { openReqModal: true } });
  };

  const enabledCats = categories.filter(c => c.enabled);
  const catScrollRef = useRef(null);

  const scrollCategories = (direction) => {
    if (!catScrollRef.current) return;
    catScrollRef.current.scrollBy({ left: direction * 220, behavior: 'smooth' });
  };

  // subcategories for currently selected category
  const subcats = useMemo(() => {
    if (!selCat) return [];
    return categories.find(c => c.id === selCat)?.subs || [];
  }, [selCat, categories]);

  // reset subcat when category changes
  const handleCatChange = (catId) => {
    setSelCat(catId);
    setSelSubcat('');
  };

  const filtered = useMemo(() => {
    let list = experts.filter(p => {
      const catMatch    = !selCat    || String(p.catId) === String(selCat);
      const subcatMatch = !selSubcat || (p.subcategories || []).includes(selSubcat);
      const searchMatch = !search
        || p.name.toLowerCase().includes(search.toLowerCase())
        || (p.bio || '').toLowerCase().includes(search.toLowerCase())
        || (p.city || '').toLowerCase().includes(search.toLowerCase())
        || (p.subcategories || []).some(s => s.toLowerCase().includes(search.toLowerCase()));
      return catMatch && subcatMatch && searchMatch;
    });
    const minPrice = e => { const prices = (e.slots || []).map(s => Number(s.price)).filter(n => n > 0); return prices.length ? Math.min(...prices) : (e.rate || 0); };
    if (sort === 'price-lo') list = [...list].sort((a, b) => minPrice(a) - minPrice(b));
    else if (sort === 'price-hi') list = [...list].sort((a, b) => minPrice(b) - minPrice(a));
    return list;
  }, [search, selCat, selSubcat, sort, experts]);

  const hasActiveFilters = selCat || selSubcat || selLang || search;
  const clearAll = () => { setSearch(''); setSelCat(''); setSelSubcat(''); setSelLang(''); };

  // Reset pagination when filters change
  useEffect(() => {
    setExpertsPage(0);
  }, [selCat, selSubcat, selLang, search]);

  return (
    <div>
      {/* ── Hero ── */}
      <section className="pattern-bg" style={{ padding: `32px 0 ${hasActiveFilters ? '16px' : '56px'}`, textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 900px 500px at 50% 120%, rgba(11,110,101,0.07) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div className="container" style={{ position: 'relative' }}>

          {/* ── Tagline badge ── */}
          <div style={{ display: 'inline-block', background: 'var(--saffron-lt)', color: 'var(--saffron)', fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', padding: '5px 16px', borderRadius: 999, marginBottom: 28 }}>
            The Premier Marketplace for Learning from Experts
          </div>

          {/* ── Multi-filter search bar ── */}
          <div style={{ maxWidth: 860, margin: '0 auto 24px', background: 'var(--surface)', border: '1.5px solid var(--border)', borderRadius: 16, boxShadow: 'var(--shadow)', overflow: 'hidden' }}>

            {/* Top row: keyword search */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '16px 16px', borderBottom: '1px solid var(--border2)' }}>
              <Search size={16} style={{ color: 'var(--ink4)', flexShrink: 0 }} />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by skill, tutor, keyword…"
                style={{ flex: 1, border: 'none', outline: 'none', fontSize: 15, color: 'var(--ink)', background: 'transparent' }}
              />
              {hasActiveFilters && (
                <button onClick={clearAll} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--ink4)', background: 'var(--bg2)', border: '1px solid var(--border2)', borderRadius: 999, padding: '3px 10px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  <X size={11} /> Clear all
                </button>
              )}
            </div>

            {/* Bottom row: dropdowns + search button */}
              <div style={{ display: 'flex', alignItems: 'stretch', gap: 0, flexWrap: 'wrap' }}>

              {/* Category */}
                <div style={{ flex: 1, minWidth: '140px', borderRight: '1px solid var(--border2)', position: 'relative' }}>
                <div style={{ padding: '4px 14px 2px', fontSize: 10, fontWeight: 700, color: 'var(--saffron)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Category</div>
                <div style={{ display: 'flex', alignItems: 'center', paddingBottom: 2 }}>
                  <select
                    value={selCat}
                    onChange={e => handleCatChange(e.target.value)}
                    style={{ flex: 1, border: 'none', outline: 'none', fontSize: 13, color: selCat ? 'var(--ink)' : 'var(--ink4)', background: 'transparent', padding: '0 8px 8px 14px', cursor: 'pointer', fontWeight: selCat ? 600 : 400, appearance: 'none' }}
                  >
                    <option value="">All categories</option>
                    {enabledCats.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
                  </select>
                  <ChevronDown size={12} style={{ color: 'var(--ink4)', marginRight: 10, flexShrink: 0, pointerEvents: 'none' }} />
                </div>
              </div>

              {/* Subcategory */}
              <div style={{ flex: 1, minWidth: '140px', borderRight: '1px solid var(--border2)', opacity: selCat ? 1 : 0.45 }}>
                <div style={{ padding: '4px 14px 2px', fontSize: 10, fontWeight: 700, color: 'var(--teal)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Subcategory</div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <select
                    value={selSubcat}
                    onChange={e => setSelSubcat(e.target.value)}
                    disabled={!selCat}
                    style={{ flex: 1, border: 'none', outline: 'none', fontSize: 13, color: selSubcat ? 'var(--ink)' : 'var(--ink4)', background: 'transparent', padding: '0 8px 8px 14px', cursor: selCat ? 'pointer' : 'default', fontWeight: selSubcat ? 600 : 400, appearance: 'none' }}
                  >
                    <option value="">{selCat ? 'All subcategories' : 'Select category first'}</option>
                    {subcats.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <ChevronDown size={12} style={{ color: 'var(--ink4)', marginRight: 10, flexShrink: 0, pointerEvents: 'none' }} />
                </div>
              </div>

              {/* Language */}
                <div style={{ flex: 1, minWidth: '140px', borderRight: '1px solid var(--border2)' }}>
                <div style={{ padding: '4px 14px 2px', fontSize: 10, fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Language</div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <select
                    value={selLang}
                    onChange={e => setSelLang(e.target.value)}
                    style={{ flex: 1, border: 'none', outline: 'none', fontSize: 13, color: selLang ? 'var(--ink)' : 'var(--ink4)', background: 'transparent', padding: '0 8px 8px 14px', cursor: 'pointer', fontWeight: selLang ? 600 : 400, appearance: 'none' }}
                  >
                    <option value="">Any language</option>
                    {languages.map(l => <option key={l.id} value={l.name}>{l.name}</option>)}
                  </select>
                  <ChevronDown size={12} style={{ color: 'var(--ink4)', marginRight: 10, flexShrink: 0, pointerEvents: 'none' }} />
                </div>
              </div>

              {/* Search button */}
                <div style={{ display: 'flex', alignItems: 'center', padding: '8px 12px', marginLeft: 'auto' }}>
                <button className="btn btn-primary" style={{ borderRadius: 10, padding: '10px 22px', fontSize: 14, fontWeight: 700 }}>
                  Search
                </button>
              </div>
            </div>
          </div>

          {/* Active filter pills */}
          {hasActiveFilters && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', marginBottom: 16 }}>
              {selCat && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'var(--saffron-lt)', color: 'var(--saffron)', border: '1px solid var(--saffron)', borderRadius: 999, padding: '4px 12px', fontSize: 12, fontWeight: 600 }}>
                  {enabledCats.find(c=>c.id===selCat)?.icon} {enabledCats.find(c=>c.id===selCat)?.name}
                  <X size={11} style={{ cursor: 'pointer' }} onClick={() => { setSelCat(''); setSelSubcat(''); }} />
                </span>
              )}
              {selSubcat && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'var(--teal-lt)', color: 'var(--teal)', border: '1px solid var(--teal)', borderRadius: 999, padding: '4px 12px', fontSize: 12, fontWeight: 600 }}>
                  {selSubcat}
                  <X size={11} style={{ cursor: 'pointer' }} onClick={() => setSelSubcat('')} />
                </span>
              )}
              {selLang && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#ede9fe', color: '#7c3aed', border: '1px solid #c4b5fd', borderRadius: 999, padding: '4px 12px', fontSize: 12, fontWeight: 600 }}>
                  🗣 {selLang}
                  <X size={11} style={{ cursor: 'pointer' }} onClick={() => setSelLang('')} />
                </span>
              )}
              {search && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'var(--bg2)', color: 'var(--ink2)', border: '1px solid var(--border)', borderRadius: 999, padding: '4px 12px', fontSize: 12, fontWeight: 600 }}>
                  "{search}"
                  <X size={11} style={{ cursor: 'pointer' }} onClick={() => setSearch('')} />
                </span>
              )}
            </div>
          )}

          {/* No results — shown immediately below filter pills */}
          {hasActiveFilters && filtered.length === 0 && (
            <div style={{ textAlign: 'center', padding: '32px 0 8px' }}>
              <div style={{ fontSize: 36, marginBottom: 10 }}>🔍</div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--ink)', marginBottom: 6 }}>No experts found</div>
              <div style={{ fontSize: 14, color: 'var(--ink3)', marginBottom: 16 }}>Try adjusting your search, or post your requirement and let experts come to you.</div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                <button onClick={clearAll} className="btn btn-secondary btn-sm">Clear all filters</button>
                <button onClick={handlePostRequirementClick} className="btn btn-primary btn-sm">📋 Post Your Requirement →</button>
              </div>
            </div>
          )}

          {/* ── Hero heading + subtitle — hidden while filtering/searching ── */}
          {!hasActiveFilters && (
            <>
              <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 48, fontWeight: 700, color: 'var(--ink)', lineHeight: 1.15, maxWidth: 640, margin: '28px auto 12px' }}>
                Learn from the <span style={{ color: 'var(--saffron)' }}>Finest Experts.</span><br />On Your Schedule.
              </h1>
              <p style={{ fontSize: 16, color: 'var(--ink3)', maxWidth: 480, margin: '0 auto 0', lineHeight: 1.65 }}>
                Book 1:1 or Group Sessions across language, music, yoga, coding, counseling and more.
              </p>
            </>
          )}
        </div>
      </section>


      {/* ── Categories (Only show when NOT searching) ── */}
{!search && (
  <section style={{ padding: '52px 0 28px' }}>
    <div className="container">
      <div className="flex items-center justify-between mb-20">
        <h2 style={{ fontSize: 26, fontWeight: 700 }}>Top Categories</h2>
      </div>

      {/* Mobile: grid layout, Desktop: carousel */}
      <div style={{ display: 'none' }} className="cat-mobile-grid">
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 16,
        }}>
          {enabledCats.map(c => (
            <div key={c.id}
              onClick={() => setSelCat(s => s === c.id ? '' : c.id)}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                cursor: 'pointer', padding: '16px 8px',
                borderRadius: 16,
                background: selCat === c.id ? 'var(--saffron-lt)' : 'var(--surface)',
                border: `1.5px solid ${selCat === c.id ? 'var(--saffron)' : 'var(--border)'}`,
                transition: 'var(--transition)',
              }}
            >
              <div style={{
                width: 56, height: 56, borderRadius: '50%',
                background: c.bg || 'var(--bg2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 28, marginBottom: 8,
              }}>
                {c.icon}
              </div>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink)', textAlign: 'center', lineHeight: 1.3 }}>{c.name}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Desktop carousel */}
      <div className="cat-desktop-carousel" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          type="button"
          onClick={() => scrollCategories(-1)}
          aria-label="Scroll categories left"
          style={{ width: 40, height: 40, borderRadius: 999, border: '1px solid var(--border)', background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--ink4)', flexShrink: 0 }}
        >
          <ArrowLeft size={18} />
        </button>
        <div ref={catScrollRef} style={{ display: 'flex', gap: 24, overflowX: 'auto', flex: 1, scrollBehavior: 'smooth', paddingBottom: 8, scrollbarWidth: 'none' }}>
          {enabledCats.map(c => (
            <div key={c.id}
              onClick={() => setSelCat(s => s === c.id ? '' : c.id)}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', transition: 'var(--transition)', minWidth: 130, flexShrink: 0, padding: '12px 8px', borderRadius: 16, background: selCat === c.id ? 'var(--saffron-lt)' : 'transparent', border: `1.5px solid ${selCat === c.id ? 'var(--saffron)' : 'transparent'}` }}
              onMouseEnter={e => { if (selCat !== c.id) { e.currentTarget.style.transform = 'translateY(-4px)'; } }}
              onMouseLeave={e => { if (selCat !== c.id) { e.currentTarget.style.transform = 'none'; } }}
            >
              <div style={{ width: 90, height: 90, borderRadius: '50%', background: selCat === c.id ? 'var(--saffron-lt)' : c.bg || 'var(--bg2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 40, marginBottom: 12, transition: 'var(--transition)' }}>
                {c.icon}
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', textAlign: 'center' }}>{c.name}</div>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={() => scrollCategories(1)}
          aria-label="Scroll categories right"
          style={{ width: 40, height: 40, borderRadius: 999, border: '1px solid var(--border)', background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--ink4)', flexShrink: 0 }}
        >
          <ArrowRight size={18} />
        </button>
      </div>
    </div>
  </section>
)}

      {/* ── Subcategory pills ── */}
      {selCat && subcats.length > 0 && (
        <div style={{ padding: '0 0 12px', overflow: 'hidden' }}>
          <div className="container">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
              <button
                onClick={() => setSelSubcat('')}
                style={{
                  padding: '5px 14px', borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  border: `1.5px solid ${!selSubcat ? 'var(--saffron)' : 'var(--border)'}`,
                  background: !selSubcat ? 'var(--saffron-lt)' : 'var(--surface)',
                  color: !selSubcat ? 'var(--saffron)' : 'var(--ink3)',
                }}
              >All</button>
              {subcats.map(s => (
                <button
                  key={s}
                  onClick={() => setSelSubcat(v => v === s ? '' : s)}
                  style={{
                    padding: '5px 14px', borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    border: `1.5px solid ${selSubcat === s ? 'var(--teal)' : 'var(--border)'}`,
                    background: selSubcat === s ? 'var(--teal-lt, #e6f7f5)' : 'var(--surface)',
                    color: selSubcat === s ? 'var(--teal)' : 'var(--ink3)',
                  }}
                >{s}</button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Experts ── */}
      {filtered.length > 0 && (
        <section style={{ padding: '16px 0 64px' }}>
          <div className="container">
            <div className="flex items-center justify-between mb-20">
              <div>
                <h2 style={{ fontSize: 22, fontWeight: 700 }}>
                  {hasActiveFilters ? 'Search Results' : 'Top Experts'}
                  <span style={{ fontSize: 13, color: 'var(--ink4)', fontWeight: 400, marginLeft: 10 }}>({filtered.length})</span>
                </h2>
              </div>
              <select value={sort} onChange={e => setSort(e.target.value)} className="form-select" style={{ width: 170 }}>
                <option value="rating">Top Rated</option>
                <option value="price-lo">Price: Low–High</option>
                <option value="price-hi">Price: High–Low</option>
              </select>
            </div>

            <div className="grid-4">
              {contentLoading
                ? Array.from({ length: 8 }).map((_, i) => (
                    <div key={i} className="card" style={{ padding: 20, minHeight: 220 }}>
                      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 14 }}>
                        <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'var(--bg2)', flexShrink: 0, animation: 'pulse 1.4s ease-in-out infinite' }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ height: 14, background: 'var(--bg2)', borderRadius: 6, marginBottom: 8, width: '65%', animation: 'pulse 1.4s ease-in-out infinite' }} />
                          <div style={{ height: 11, background: 'var(--bg2)', borderRadius: 6, width: '45%', animation: 'pulse 1.4s ease-in-out infinite' }} />
                        </div>
                      </div>
                      <div style={{ height: 11, background: 'var(--bg2)', borderRadius: 6, marginBottom: 8, animation: 'pulse 1.4s ease-in-out infinite' }} />
                      <div style={{ height: 11, background: 'var(--bg2)', borderRadius: 6, width: '80%', marginBottom: 16, animation: 'pulse 1.4s ease-in-out infinite' }} />
                      <div style={{ height: 36, background: 'var(--bg2)', borderRadius: 8, animation: 'pulse 1.4s ease-in-out infinite' }} />
                    </div>
                  ))
                : filtered.slice(expertsPage * EXPERTS_PER_PAGE, (expertsPage + 1) * EXPERTS_PER_PAGE).map(p => (
                    <ExpertCard key={p.id} expert={p} onClick={() => navigate(`/expert/${p.id}`)} />
                  ))
              }
            </div>

            {/* Pagination arrows */}
            {filtered.length > EXPERTS_PER_PAGE && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginTop: 32 }}>
                <button
                  onClick={() => setExpertsPage(p => Math.max(0, p - 1))}
                  disabled={expertsPage === 0}
                  style={{
                    width: 40, height: 40, borderRadius: 999, border: '1px solid var(--border)',
                    background: expertsPage === 0 ? 'var(--bg2)' : 'var(--surface)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: expertsPage === 0 ? 'default' : 'pointer',
                    color: expertsPage === 0 ? 'var(--ink4)' : 'var(--ink)',
                    transition: 'var(--transition)',
                  }}
                >
                  <ArrowLeft size={18} />
                </button>

                <div style={{ fontSize: 13, color: 'var(--ink3)', minWidth: 120, textAlign: 'center' }}>
                  Page {expertsPage + 1} of {Math.ceil(filtered.length / EXPERTS_PER_PAGE)}
                </div>

                <button
                  onClick={() => setExpertsPage(p => p + 1)}
                  disabled={expertsPage >= Math.ceil(filtered.length / EXPERTS_PER_PAGE) - 1}
                  style={{
                    width: 40, height: 40, borderRadius: 999, border: '1px solid var(--border)',
                    background: expertsPage >= Math.ceil(filtered.length / EXPERTS_PER_PAGE) - 1 ? 'var(--bg2)' : 'var(--surface)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: expertsPage >= Math.ceil(filtered.length / EXPERTS_PER_PAGE) - 1 ? 'default' : 'pointer',
                    color: expertsPage >= Math.ceil(filtered.length / EXPERTS_PER_PAGE) - 1 ? 'var(--ink4)' : 'var(--ink)',
                    transition: 'var(--transition)',
                  }}
                >
                  <ArrowRight size={18} />
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── CTA ── */}
      <section style={{ background: 'linear-gradient(135deg, var(--teal) 0%, #05514A 100%)', padding: '64px 0', textAlign: 'center' }}>
        <div className="container">
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 38, fontWeight: 700, color: 'white', marginBottom: 12 }}>Are you an expert?</h2>
          <p style={{ fontSize: 17, color: 'rgba(255,255,255,0.75)', marginBottom: 32, maxWidth: 480, margin: '0 auto 32px' }}>
            Join our growing community of expert tutors. Set your rates, own your calendar.
          </p>
          <button onClick={() => navigate('/auth?mode=register&role=expert')} className="btn btn-lg" style={{ background: 'white', color: 'var(--teal)', fontWeight: 700, gap: 8 }}>
            Start Teaching Today <ArrowRight size={18} />
          </button>
        </div>
      </section>

      {/* ── Reasons to Join Us ── */}
      <section style={{ padding: '72px 0', background: 'var(--bg2)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <div className="eyebrow mb-4" style={{ color: 'var(--saffron)' }}>WHY MAKEMYEXPERT</div>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 42, fontWeight: 700, color: 'var(--ink)', marginBottom: 10 }}>Reasons to Join Us</h2>
            <p style={{ fontSize: 16, color: 'var(--ink3)', maxWidth: 480, margin: '0 auto' }}>Everything you need to learn, grow, and connect — in one place.</p>
          </div>

          {/* Stats card */}
          {/* <div style={{ display: 'flex', alignItems: 'center', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, boxShadow: 'var(--shadow)', flexWrap: 'wrap', justifyContent: 'center', maxWidth: 640, margin: '0 auto 40px' }}>
            {[['2,400+','Verified Experts'],['18,000+','Sessions Booked'],['8','Cities + Online'],['4.8★','Avg Rating']].map(([num, lbl], i) => (
              <div key={lbl} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                {i > 0 && <div style={{ width: 1, height: 40, background: 'var(--border2)', flexShrink: 0 }} />}
                <div style={{ textAlign: 'center', flex: 1, padding: '16px 28px' }}>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 700, color: 'var(--ink)' }}>{num}</div>
                  <div style={{ fontSize: 12, color: 'var(--ink4)', marginTop: 2, whiteSpace: 'nowrap' }}>{lbl}</div>
                </div>
              </div>
            ))}
          </div> */}

          {/* Reason cards */}
          <div className="grid-3" style={{ gap: 16, marginTop: 40 }}>
            {[
              { icon: '🎯', title: 'Verified Experts Only',  desc: 'Every expert on our platform is vetted for expertise, experience, and professionalism.',  accent: 'var(--saffron-lt)' },
              { icon: '📅', title: 'Flexible Scheduling',    desc: 'Book sessions at a time that works for you — mornings, evenings, or weekends.',           accent: '#ede9fe' },
              { icon: '💬', title: '1:1 & Group Sessions',   desc: 'Choose personalised 1:1 coaching or join group sessions for a collaborative experience.',  accent: 'var(--teal-lt)' },
              { icon: '🌐', title: 'Learn from Anywhere',    desc: 'Fully online sessions mean you can connect with the best experts regardless of location.',  accent: '#dcfce7' },
              { icon: '₹',  title: 'Transparent Pricing',    desc: 'No hidden fees. See exact pricing upfront and pay only for what you book.',                 accent: '#fef9c3' },
              { icon: '🏆', title: 'Diverse Categories',     desc: 'From language and music to yoga, coding, and counseling — find expertise in any domain.',   accent: '#fee2e2' },
            ].map(({ icon, title, desc, accent }) => (
              <div key={title} style={{ background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 16, padding: 24 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>{icon}</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)', marginTop: 16 }}>{title}</div>
                <div style={{ fontSize: 13, color: 'var(--ink3)', marginTop: 6, lineHeight: 1.6 }}>{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Customer Care Contacts ── */}
      {customerCareNumbers.length > 0 && (
        <section style={{ padding: '72px 0', background: 'linear-gradient(135deg, var(--teal-lt) 0%, #f0fdf4 100%)' }}>
          <div className="container">
            <div style={{ textAlign: 'center', marginBottom: 52 }}>
              <div className="eyebrow mb-4" style={{ color: 'var(--teal)' }}>CUSTOMER SUPPORT</div>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 36, fontWeight: 700, color: 'var(--ink)', marginBottom: 12 }}>Get in Touch</h2>
              <p style={{ fontSize: 16, color: 'var(--ink3)', maxWidth: 500, margin: '0 auto' }}>Have questions? Our support team is here to help you 24/7</p>
            </div>

            <div className="grid-3" style={{ gap: 24, maxWidth: 900, margin: '0 auto' }}>
              {customerCareNumbers.map((contact) => (
                <div key={contact.id} style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 16, padding: 32, textAlign: 'center', boxShadow: 'var(--shadow)' }}>
                  {contact.country && (
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink4)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 16 }}>
                      📍 {contact.country}
                    </div>
                  )}

                  {contact.phone && (
                    <a href={`tel:${contact.phone.replace(/\D/g, '')}`}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, padding: '12px 0', marginBottom: 12,
                        textDecoration: 'none', borderRadius: 10, color: 'var(--ink)', transition: 'var(--transition)'
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg2)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#e0f2fe', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Phone size={18} style={{ color: '#0369a1' }} />
                      </div>
                      <div style={{ textAlign: 'left' }}>
                        <div style={{ fontSize: 11, color: 'var(--ink4)', fontWeight: 600 }}>PHONE</div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)' }}>{contact.phone}</div>
                      </div>
                    </a>
                  )}

                  {contact.whatsapp && (
                    <a href={`https://wa.me/${contact.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer"
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, padding: '12px 0',
                        textDecoration: 'none', borderRadius: 10, color: 'var(--ink)', transition: 'var(--transition)'
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg2)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <MessageCircle size={18} style={{ color: '#16a34a' }} />
                      </div>
                      <div style={{ textAlign: 'left' }}>
                        <div style={{ fontSize: 11, color: 'var(--ink4)', fontWeight: 600 }}>WHATSAPP</div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)' }}>{contact.whatsapp}</div>
                      </div>
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── FAQ ── */}
      <section id="faq" style={{ padding: '72px 0', background: 'var(--bg)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: 52 }}>
            <div className="eyebrow mb-4">Got questions?</div>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 36, fontWeight: 700, color: 'var(--ink)' }}>Frequently Asked Questions</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 40, alignItems: 'start' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                <span style={{ fontSize: 22 }}>📚</span>
                <h3 style={{ fontWeight: 700, fontSize: 18, color: 'var(--ink)' }}>For Learners</h3>
              </div>
              <FaqAccordion items={FAQ_LEARNER} />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                <span style={{ fontSize: 22 }}>🎓</span>
                <h3 style={{ fontWeight: 700, fontSize: 18, color: 'var(--ink)' }}>For Tutors / Experts</h3>
              </div>
              <FaqAccordion items={FAQ_TUTOR} />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
